import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { useOrg } from './OrgContext.js';
import { C } from '../lib/theme.js';
import basilLogo from '../assets/logo_basil.png';
import pcLogo from '../assets/phuket-cleanse-white.webp';
import { BasilBot } from './BasilBot.js';
import { applyTheme, applySavedTheme } from './ThemePicker.js';

interface NavItem {
  to: string;
  label: string;
  glyph: string;
}
const NAV: NavItem[] = [
  { to: '/', label: 'Home', glyph: '◈' },
  { to: '/results', label: 'Results', glyph: '◎' },
  { to: '/campaigns', label: 'Campaigns', glyph: '▦' },
  { to: '/bookings', label: 'Bookings', glyph: '฿' },
  { to: '/studio', label: 'Studio', glyph: '✦' },
  { to: '/launch', label: 'Launch', glyph: '↗' },
  { to: '/settings', label: 'Settings', glyph: '⚙' },
];

const SCREEN_LABEL: Record<string, string> = {
  '/': 'home',
  '/results': 'results',
  '/campaigns': 'campaigns',
  '/bookings': 'bookings',
  '/studio': 'studio',
  '/studio/library': 'studio · library',
  '/studio/copy': 'studio · compose',
  '/studio/create': 'studio · style image',
  '/launch': 'launch',
  '/settings': 'settings',
  '/settings/connections': 'settings · connections',
  '/settings/figures': 'settings · figures',
  '/settings/activity': 'settings · activity',
};

export function AppShell() {
  const { pathname } = useLocation();
  const { activeOrg } = useOrg();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Phuket Cleanse is locked to the 'dim' theme; other orgs keep the picker.
  // Don't touch the user's saved preference — just override the applied theme.
  useEffect(() => {
    if (activeOrg?.slug === 'phuket-cleanse') applyTheme('dim');
    else applySavedTheme();
  }, [activeOrg]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const screenLabel = SCREEN_LABEL[pathname] ?? pathname.replace('/', '');

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: C.base }}>
      <IconRail />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Header screenLabel={screenLabel} onSearch={() => setPaletteOpen(true)} />
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Outlet />
        </div>
      </div>
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
      <BasilBot />
    </div>
  );
}

/**
 * The nav THIS workspace may see. Bookings is the Cloudbeds reservation feed —
 * only a tenant with a booking system has one, and its ฿ glyph is Thai baht.
 *
 * Both the rail and the command palette must filter through here. They didn't:
 * the rail hid Bookings correctly while the palette searched the raw NAV, so a
 * Propty user could type "book" and walk straight into Phuket Cleanse's screen.
 */
function navForOrg(slug: string | null | undefined): NavItem[] {
  return NAV.filter((it) => it.to !== '/bookings' || slug === 'phuket-cleanse');
}

const RAIL_ITEM_H = 56;
const RAIL_GAP = 4;
// The Basil leaf-logo gradient: deep green (bottom-left) → bright lime (top-right).
const LOGO_GRADIENT = 'linear-gradient(45deg, #3aa14e 0%, #7fce52 55%, #a7e05a 100%)';
const LOGO_ARROW = '#7ac94f';

function IconRail() {
  const { signOut } = useAuth();
  const { pathname } = useLocation();
  const { activeOrg } = useOrg();
  const nav = navForOrg(activeOrg?.slug);
  const activeIndex = nav.findIndex((it) =>
    it.to === '/' ? pathname === '/' : pathname.startsWith(it.to),
  );
  return (
    <div
      style={{
        width: 104,
        flexShrink: 0,
        background: C.rail,
        borderRight: `1px solid ${C.lineSoft}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '16px 0 14px',
      }}
    >
      <img
        src={basilLogo}
        alt="Basil"
        style={{ width: 42, height: 42, objectFit: 'contain', marginBottom: 16 }}
      />
      <div
        style={{
          position: 'relative',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          gap: RAIL_GAP,
        }}
      >
        {/* Sliding basil-green highlight that eases up/down between items */}
        {activeIndex >= 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 12,
              right: 10,
              height: RAIL_ITEM_H,
              background: LOGO_GRADIENT,
              transform: `translateY(${activeIndex * (RAIL_ITEM_H + RAIL_GAP)}px)`,
              transition: 'transform .42s cubic-bezier(.16,1,.3,1)',
              boxShadow: '0 6px 18px rgba(60,160,75,.4)',
              zIndex: 0,
            }}
          >
            {/* arrow on the right edge, pointing toward the content */}
            <span
              style={{
                position: 'absolute',
                right: -8,
                top: '50%',
                marginTop: -8,
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderLeft: `8px solid ${LOGO_ARROW}`,
              }}
            />
          </div>
        )}
        {nav.map((item, i) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={item.label}
            style={{ textDecoration: 'none', position: 'relative', zIndex: 1, width: '100%' }}
          >
            <div
              style={{
                width: '100%',
                height: RAIL_ITEM_H,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                cursor: 'pointer',
                color: i === activeIndex ? '#ffffff' : C.muted,
                transition: 'color .3s ease',
              }}
            >
              <div style={{ fontSize: 22, lineHeight: 1 }}>{item.glyph}</div>
              <div style={{ font: `500 10px 'IBM Plex Mono',monospace`, letterSpacing: '.03em' }}>
                {item.label}
              </div>
            </div>
          </NavLink>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void signOut()}
        title="Sign out"
        aria-label="Sign out"
        style={{
          marginTop: 'auto',
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `1px solid ${C.line}`,
          background: C.surface,
          color: C.muted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </button>
    </div>
  );
}

function Header({ screenLabel, onSearch }: { screenLabel: string; onSearch: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 26px',
        height: 64,
        borderBottom: `1px solid ${C.lineSoft}`,
        flexShrink: 0,
        background: C.base,
      }}
    >
      <OrgSwitcher />
      <div style={{ font: `500 12px 'IBM Plex Mono',monospace`, letterSpacing: '.04em' }}>
        <span style={{ color: C.faint }}>basil</span> <span style={{ color: C.lineStrong }}>/</span>{' '}
        <span style={{ color: C.basil }}>{screenLabel}</span>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          onClick={onSearch}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '7px 12px',
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            color: C.muted,
            font: `500 11.5px 'IBM Plex Mono',monospace`,
            cursor: 'pointer',
          }}
        >
          SEARCH
          <span
            style={{
              padding: '1px 5px',
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 4,
              color: C.secondary,
            }}
          >
            ⌘K
          </span>
        </div>
      </div>
    </div>
  );
}

function OrgSwitcher() {
  const { orgs, activeOrg, setOrg } = useOrg();
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '7px 11px',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 9,
          cursor: 'pointer',
        }}
      >
        {activeOrg?.slug === 'phuket-cleanse' ? (
          <span
            aria-label={activeOrg.name}
            style={{
              display: 'inline-block',
              width: 101,
              height: 23,
              background: C.primary,
              WebkitMaskImage: `url(${pcLogo})`,
              maskImage: `url(${pcLogo})`,
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'left center',
              maskPosition: 'left center',
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
            }}
          />
        ) : (
          <span style={{ font: `600 12.5px 'IBM Plex Mono',monospace`, color: C.primary }}>
            {activeOrg ? activeOrg.slug : 'no org'}
          </span>
        )}
        <span style={{ color: C.muted, fontSize: 11 }}>⌄</span>
      </div>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              minWidth: 200,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: 5,
              zIndex: 50,
              boxShadow: '0 12px 32px rgba(0,0,0,.5)',
            }}
          >
            {orgs.length === 0 && (
              <div style={{ padding: '8px 10px', color: C.muted, fontSize: 12.5 }}>
                No organizations
              </div>
            )}
            {orgs.map((o) => (
              <div
                key={o.id}
                onClick={() => {
                  setOrg(o.id);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '8px 10px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  color: o.id === activeOrg?.id ? C.primary : C.secondary,
                  background: o.id === activeOrg?.id ? C.surface2 : 'transparent',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: o.id === activeOrg?.id ? C.basil : C.faint,
                  }}
                />
                <span style={{ fontSize: 13 }}>{o.name}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    font: `500 10px 'IBM Plex Mono',monospace`,
                    color: C.faint,
                  }}
                >
                  {o.default_currency}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { activeOrg } = useOrg();
  const [q, setQ] = useState('');
  // Same org filter as the rail — searching must never reach another tenant's screen.
  const items = navForOrg(activeOrg?.slug).filter((n) =>
    n.label.toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.55)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '14vh',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px,92vw)',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,.6)',
        }}
      >
        <input
          autoFocus
          className="field"
          placeholder="Jump to…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            border: 'none',
            borderRadius: 0,
            borderBottom: `1px solid ${C.line}`,
            padding: '15px 18px',
            fontSize: 15,
          }}
        />
        <div style={{ padding: 6 }}>
          {items.map((n) => (
            <div
              key={n.to}
              onClick={() => {
                navigate(n.to);
                onClose();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 8,
                cursor: 'pointer',
                color: C.secondary,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.surface2)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{n.glyph}</span>
              <span style={{ fontSize: 13.5 }}>{n.label}</span>
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ padding: '10px 12px', color: C.faint, fontSize: 13 }}>No matches</div>
          )}
        </div>
      </div>
    </div>
  );
}
